import type {BaseLogger} from '@wix/document-services-types'
import {ReportableError} from './ReportableIssue'
import _ from 'lodash'

export class Deferred<T> {
    public resolve!: (value: T | PromiseLike<T>) => void
    public reject!: (reason?: any) => void
    public promise: Promise<T>

    constructor() {
        this.promise = new Promise<T>((res, rej) => {
            this.resolve = res
            this.reject = rej
        })
    }
}

export interface DeferReq<T> {
    deferred: Deferred<T>
    timeout: any
    options?: RegisterOptions
}

export interface Callbacks<T> {
    onResolve?(key: T): void
    onReject?(key: T): void
    onTimeout?(key: T, cancelRejectOnTimeout: () => void): void | Promise<void>
}
type Extras = Record<string, any>

const notifierTimeout = 1000 * 10

interface RegisterOptions {
    extras?: Extras
    rejectionOnTimeoutCb?(error: ReportableError): void | Promise<void>
    timeout?: number
}

export class Notifier<K, V = void, E = Error> {
    constructor(
        private readonly logger?: BaseLogger,
        private readonly callbacks: Callbacks<K> = {},
        private readonly timeout: number = notifierTimeout
    ) {}

    private readonly map: Map<K, DeferReq<V>> = new Map<K, DeferReq<V>>()

    private pop(key: K): DeferReq<V> | undefined {
        const req = this.map.get(key)
        if (req) {
            this.map.delete(key)
            clearTimeout(req.timeout)
        }
        return req
    }

    private logDeferredEnded = (key: K, isResolved: boolean) => {
        this.logger?.interactionEnded('Deferred', {
            tags: {
                action: isResolved ? 'resolve' : 'reject'
            },
            extras: {
                key
            }
        })
    }

    private createATimeout = (key: K, deferred: Deferred<V>, options?: RegisterOptions) => {
        const timeoutReference = setTimeout(async () => {
            let shouldRejectOnTimeout = true
            const cancelRejectOnTimeout = () => {
                shouldRejectOnTimeout = false
            }

            await this.callbacks.onTimeout?.(key, cancelRejectOnTimeout)
            if (shouldRejectOnTimeout) {
                this.map.delete(key)
                const deferredTimeoutError = new ReportableError({
                    message: `Request timed out for correlationId ${key}`,
                    errorType: 'DeferredTimeoutError',
                    tags: {
                        source: 'Notifier'
                    },
                    extras: {
                        key,
                        ...options?.extras
                    }
                })
                this.logger?.captureError(deferredTimeoutError)
                deferred.reject(deferredTimeoutError)
                await options?.rejectionOnTimeoutCb?.(deferredTimeoutError)
            }
        }, options?.timeout ?? this.timeout)

        if (_.isNumber(timeoutReference)) {
            return timeoutReference
        }

        return (timeoutReference[Symbol.toPrimitive] as Function)()
    }

    register(key: K, options?: RegisterOptions): Promise<V> {
        const deferred = new Deferred<V>()
        const timeoutHandle = this.createATimeout(key, deferred, options)
        this.map.set(key, {deferred, timeout: timeoutHandle, options})
        this.logger?.interactionStarted('Deferred', {
            tags: {
                action: 'register'
            },
            extras: {
                key,
                timeout: timeoutHandle,
                ...options?.extras
            }
        })
        return deferred.promise
    }

    resetTimer(key: K, optionsOverrides?: RegisterOptions) {
        if (!this.isRegistered(key)) {
            return
        }
        const {deferred, timeout, options} = this.map.get(key)!
        clearTimeout(timeout)
        const newTimeoutHandle = this.createATimeout(key, deferred, _.merge(options, optionsOverrides))
        this.map.set(key, {deferred, timeout: newTimeoutHandle, options})
    }

    resolve(key: K, value: V) {
        const req = this.pop(key)
        if (req) {
            this.callbacks.onResolve?.(key)
            req.deferred.resolve(value)
            this.logDeferredEnded(key, true)
        }
    }

    reject(key: K, error?: E) {
        const req = this.pop(key)
        if (req) {
            this.callbacks.onReject?.(key)
            req.deferred.reject(error)
            this.logDeferredEnded(key, false)
        }
    }

    isRegistered(key: K): boolean {
        return this.map.has(key)
    }

    async waitForCurrentPending(): Promise<void> {
        if (this.map.size === 0) {
            return Promise.resolve()
        }
        this.logger?.interactionStarted('waitForCurrentPendingDeferred', {
            extras: {
                keys: [...this.map.keys()]
            }
        })
        const allPromises = _.map([...this.map.values()], 'deferred.promise')
        await Promise.all(allPromises)
    }
}
